import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import { crmPostventaV2Api } from "./crmPostventaV2Api";
import { CRM_POSTVENTA_V2_SECTIONS, getSectionMeta } from "./processCatalog";
import ProcessQueueTable from "./components/ProcessQueueTable";
import ProcessActionModal from "./components/ProcessActionModal";
import "./CRMPostventaV2Procesos.css";

const makeToday = () => new Date().toISOString().slice(0, 10);

const PROCESS_SLUGS = CRM_POSTVENTA_V2_SECTIONS.filter((item) => !item.isSpecial).map((item) => item.slug);

export default function CRMPostventaV2Procesos({ slug = "zoco-check", embedded = false }) {
  const section = getSectionMeta(slug);
  const [fecha, setFecha] = useState(makeToday());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [existingEvidences, setExistingEvidences] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const loadRows = async () => {
    setLoading(true);
    try {
      if (slug === "historial") {
        const responses = await Promise.all(
          PROCESS_SLUGS.map((processSlug) => crmPostventaV2Api.obtenerCola(processSlug, { fecha }))
        );
        const merged = responses.flatMap((response, index) =>
          (response?.filas || []).map((item) => ({
            ...item,
            procesoSlug: PROCESS_SLUGS[index],
            procesoLabel: getSectionMeta(PROCESS_SLUGS[index]).shortTitle,
          }))
        );
        setRows(merged.sort((a, b) => String(a.fechaProximaAccion || "").localeCompare(String(b.fechaProximaAccion || ""))));
      } else {
        const response = await crmPostventaV2Api.obtenerCola(slug, { fecha });
        setRows((response?.filas || []).map((item) => ({ ...item, procesoSlug: slug, procesoLabel: section.shortTitle })));
      }
    } catch (error) {
      Swal.fire(section.shortTitle, error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, [slug, fecha]);

  const openDetail = async (item) => {
    try {
      setSubmitting(true);
      const processSlug = item.procesoSlug || slug;
      const [detailResponse, evidencesResponse] = await Promise.all([
        crmPostventaV2Api.obtenerDetalle(processSlug, item.id),
        crmPostventaV2Api.obtenerEvidencias(item.id),
      ]);
      setDetail({
        ...detailResponse,
        instancia: {
          ...detailResponse?.instancia,
          procesoCodigo: detailResponse?.instancia?.procesoCodigo || processSlug,
          procesoSlug: processSlug,
        },
      });
      setExistingEvidences(evidencesResponse || []);
      setModalOpen(true);
    } catch (error) {
      Swal.fire(section.shortTitle, error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerator = async (dryRun) => {
    try {
      setSubmitting(true);
      const action = section.supportsReconcile ? crmPostventaV2Api.reconciliar : crmPostventaV2Api.generar;
      const response = await action(slug, { dryRun, fecha });
      await Swal.fire(
        dryRun ? "Dry run completado" : "Operación completada",
        [
          response?.message || "Sin mensaje.",
          `Candidatos: ${response?.candidatos || 0}`,
          `Crear: ${response?.crear || 0}`,
          `Ya existentes: ${response?.yaExistentes || 0}`,
          `Bloqueados: ${response?.bloqueados || 0}`,
        ].join("\n"),
        "success"
      );
      await loadRows();
    } catch (error) {
      Swal.fire(section.shortTitle, error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAction = async (payload) => {
    if (!detail?.instancia?.id) return;

    try {
      setSubmitting(true);
      const response = await crmPostventaV2Api.ejecutarAccion(detail.instancia.procesoSlug || slug, detail.instancia.id, payload);
      await Swal.fire(section.shortTitle, response?.message || "Acción registrada.", "success");
      setModalOpen(false);
      setDetail(null);
      setExistingEvidences([]);
      await loadRows();
    } catch (error) {
      Swal.fire(section.shortTitle, error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const summary = useMemo(
    () => ({
      total: rows.length,
      derivados: rows.filter((item) => item.derivado).length,
      checklistPendiente: rows.filter((item) => item.checklist?.length && !item.checklistCompletado).length,
    }),
    [rows]
  );

  const content = (
    <>
      <section className={`crm-postventa-v2-panel p-4${embedded ? "" : " mt-4"}`}>
        <div className="d-flex flex-wrap gap-3 justify-content-between align-items-end">
          <div>
            <div className="crm-postventa-kicker">{embedded ? "Seguimiento 2" : "CRM Postventa V2"}</div>
            <h2 className="mb-1">{section.shortTitle}</h2>
            <p className="text-muted mb-0">{section.description}</p>
          </div>

          <div className="d-flex flex-wrap gap-2 align-items-end">
            <label className="d-flex flex-column gap-1">
              <span className="small fw-semibold">Fecha referencia</span>
              <input className="form-control" type="date" value={fecha} onChange={(event) => setFecha(event.target.value)} />
            </label>
            <Button variant="outline-secondary" onClick={loadRows} disabled={loading || submitting}>
              Actualizar
            </Button>
            {!section.isSpecial && (
              <>
                <Button variant="outline-primary" onClick={() => handleGenerator(true)} disabled={loading || submitting}>
                  Dry run
                </Button>
                {(section.supportsGenerate || section.supportsReconcile) && (
                  <Button variant="success" onClick={() => handleGenerator(false)} disabled={loading || submitting}>
                    {section.actionLabel || "Ejecutar"}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <section className="crm-postventa-v2-panel p-4 mt-4">
        <Alert variant="info" className="mb-3">
          Total visibles: {summary.total} | Derivadas: {summary.derivados} | Checklist pendiente: {summary.checklistPendiente}
        </Alert>

        {slug === "historial" && (
          <Alert variant="secondary">
            Esta vista consolida las colas activas de los procesos V2 para auditoría operativa rápida.
          </Alert>
        )}

        {loading ? (
          <div className="py-5 text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <ProcessQueueTable
            rows={rows}
            onManage={openDetail}
            loading={submitting}
            includeProcess={slug === "historial" || slug === "derivaciones"}
          />
        )}
      </section>
    </>
  );

  return (
    <>
      {embedded ? content : (
        <section className="crm-postventa-page" translate="no">
          <div className="container py-4">{content}</div>
        </section>
      )}

      <ProcessActionModal
        show={modalOpen}
        onHide={() => setModalOpen(false)}
        detail={detail}
        existingEvidences={existingEvidences}
        submitting={submitting}
        onSubmit={handleSubmitAction}
      />
    </>
  );
}
